import React, { useState, useEffect, useCallback, useRef, useMemo, useTransition } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box, Typography, Card, CardContent, Grid, ToggleButton, ToggleButtonGroup,
    Avatar, CircularProgress, Alert, LinearProgress, Button, Pagination, TextField, InputAdornment,
    Chip, Slider, FormControl, InputLabel, OutlinedInput, Dialog, DialogTitle, DialogContent, DialogActions, IconButton,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper
} from '@mui/material';
import {
    Visibility, Phone, Language, Refresh, Analytics, Search, Clear, Timeline, Assessment, Close as CloseIcon
} from '@mui/icons-material';

// Helper Functions
const calculateImprovementPercentage = (beforeValue, afterValue) => {
    if (!beforeValue || beforeValue === 0) return afterValue > 0 ? 100 : 0;
    return Math.round(((afterValue - beforeValue) / beforeValue) * 100);
};

const formatKpiValue = (value) => {
    if (!value && value !== 0) return '0';
    const numValue = Number(value);
    if (isNaN(numValue)) return '0';
    if (numValue >= 1000000) return (numValue / 1000000).toFixed(1) + 'M';
    if (numValue >= 1000) return (numValue / 1000).toFixed(1) + 'K';
    return Math.round(numValue).toString();
};

const formatDateRange = (timePeriod) => {
    if (!timePeriod || !timePeriod.start || !timePeriod.end) return 'N/A';
    
    const formatSingleDate = (date) => {
        if (!date || !date.day || !date.month || !date.year) return 'N/A';
        return `${String(date.day).padStart(2, '0')}.${String(date.month).padStart(2, '0')}.${date.year}`;
    };
    
    const startDate = formatSingleDate(timePeriod.start);
    const endDate = formatSingleDate(timePeriod.end);
    
    return `${startDate} - ${endDate}`;
};

const kpiOptions = [
    { value: 'Impressions', label: 'Impressions', icon: <Visibility />, color: '#2196f3' },
    { value: 'Anrufe', label: 'Anrufe', icon: <Phone />, color: '#4caf50' },
    { value: 'Website Klicks', label: 'Website Klicks', icon: <Language />, color: '#9c27b0' },
];

// Debounce helper
const useDebounce = (value, delay = 300) => {
    const [debouncedValue, setDebouncedValue] = useState(value);
    useEffect(() => {
        const handler = setTimeout(() => setDebouncedValue(value), delay);
        return () => clearTimeout(handler);
    }, [value, delay]);
    return debouncedValue;
};

// API response cache
const apiCache = new Map();

const TopPerformerPage = () => {
    // Hooks
    const navigate = useNavigate();

    // State
    const [loading, setLoading] = useState(false);
    const [kpiFilter, setKpiFilter] = useState('Impressions');
    const [performanceData, setPerformanceData] = useState([]);
    const [error, setError] = useState(null);
    const [loadingMessage, setLoadingMessage] = useState('Lade Performance-Daten...');
    const [progress, setProgress] = useState({ percentage: 0, text: '0%', processed: 0, total: 0 });
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(9); // 3x3 grid renders faster than 4x3
    const [result, setResult] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [vorherRange, setVorherRange] = useState({ min: 0, max: 1000000 });
    const [nachherRange, setNachherRange] = useState({ min: 0, max: 1000000 });
    // Add state for input values to handle typing
    const [minInputValue, setMinInputValue] = useState('');
    const [maxInputValue, setMaxInputValue] = useState('');
    const [nachherMinInputValue, setNachherMinInputValue] = useState('');
    const [nachherMaxInputValue, setNachherMaxInputValue] = useState('');
    // New state for toggle buttons
    const [navigationTarget, setNavigationTarget] = useState('performance-details');
    const [timeRangeFilter, setTimeRangeFilter] = useState('vorher');

    const abortControllerRef = useRef(null);
    const dataLoadTimeRef = useRef(null);

    // Use transition for non-blocking updates
    const [isPending, startTransition] = useTransition();

    // Modal state for activity summary
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedBusiness, setSelectedBusiness] = useState(null);
    const [modalLoading, setModalLoading] = useState(false);
    const [businessDetails, setBusinessDetails] = useState(null);

    // State for activity detail modal
    const [activityDetailOpen, setActivityDetailOpen] = useState(false);
    const [selectedActivityType, setSelectedActivityType] = useState(null);
    const [activityDates, setActivityDates] = useState([]);

    // Debounce search and filter inputs
    const debouncedSearchTerm = useDebounce(searchTerm, 250);
    const debouncedVorherRange = useDebounce(vorherRange, 300);
    const debouncedNachherRange = useDebounce(nachherRange, 300);

    // KPI value calculation functions
    const getForImpressions = useCallback((periodDataStat) => {
        if (!periodDataStat) return { total: 0, average: 0 };
        
        const total = (
            (periodDataStat.BUSINESS_IMPRESSIONS_DESKTOP_MAPS?.total || 0) +
            (periodDataStat.BUSINESS_IMPRESSIONS_DESKTOP_SEARCH?.total || 0) +
            (periodDataStat.BUSINESS_IMPRESSIONS_MOBILE_MAPS?.total || 0) +
            (periodDataStat.BUSINESS_IMPRESSIONS_MOBILE_SEARCH?.total || 0)
        );
        
        const average = (
            (periodDataStat.BUSINESS_IMPRESSIONS_DESKTOP_MAPS?.average || 0) +
            (periodDataStat.BUSINESS_IMPRESSIONS_DESKTOP_SEARCH?.average || 0) +
            (periodDataStat.BUSINESS_IMPRESSIONS_MOBILE_MAPS?.average || 0) +
            (periodDataStat.BUSINESS_IMPRESSIONS_MOBILE_SEARCH?.average || 0)
        );
        
        return { total, average };
    }, []);

    const getKpiValue = useCallback((business, period, filter) => {
        if (!business || !business[period] || !business[period].summaryStats) {
            return { total: 0, average: 0 };
        }

        const summaryStats = business[period].summaryStats;
        
        switch (filter) {
            case 'Impressions':
                return getForImpressions(summaryStats);
            case 'Anrufe':
                return { 
                    total: summaryStats.CALL_CLICKS?.total || 0, 
                    average: summaryStats.CALL_CLICKS?.average || 0 
                };
            case 'Website Klicks':
                return { 
                    total: summaryStats.WEBSITE_CLICKS?.total || 0, 
                    average: summaryStats.WEBSITE_CLICKS?.average || 0 
                };
            default:
                return { total: 0, average: 0 };
        }
    }, [getForImpressions]);

    const getImprovement = useCallback((business, filter) => {
        const beforeValue = getKpiValue(business, 'beforePeriodData', filter).total;
        const afterValue = getKpiValue(business, 'afterPeriodData', filter).total;
        return calculateImprovementPercentage(beforeValue, afterValue);
    }, [getKpiValue]);

    // Calculate range limits based on current data and KPI filter
    const vorherRangeLimits = useMemo(() => {
        if (!performanceData.length) return { min: 0, max: 1000 };

        const vorherValues = performanceData.map(business => 
            getKpiValue(business, 'beforePeriodData', kpiFilter).total
        ).filter(value => !isNaN(value) && value !== null);

        if (vorherValues.length === 0) return { min: 0, max: 1000 };

        const min = Math.min(...vorherValues);
        const max = Math.max(...vorherValues);
        
        return { 
            min: Math.floor(min), 
            max: Math.ceil(max) 
        };
    }, [performanceData, kpiFilter, getKpiValue]);

    // Calculate nachher range limits based on current data and KPI filter
    const nachherRangeLimits = useMemo(() => {
        if (!performanceData.length) return { min: 0, max: 1000 };

        const nachherValues = performanceData.map(business => 
            getKpiValue(business, 'afterPeriodData', kpiFilter).total
        ).filter(value => !isNaN(value) && value !== null);

        if (nachherValues.length === 0) return { min: 0, max: 1000 };

        const min = Math.min(...nachherValues);
        const max = Math.max(...nachherValues);
        
        return { 
            min: Math.floor(min), 
            max: Math.ceil(max) 
        };
    }, [performanceData, kpiFilter, getKpiValue]);

    // Update range when data or KPI changes
    useEffect(() => {
        if (performanceData.length > 0) {
            setVorherRange(vorherRangeLimits);
            setNachherRange(nachherRangeLimits);
            // Update input values to match the new range
            setMinInputValue(String(vorherRangeLimits.min));
            setMaxInputValue(String(vorherRangeLimits.max));
            setNachherMinInputValue(String(nachherRangeLimits.min));
            setNachherMaxInputValue(String(nachherRangeLimits.max));
        }
    }, [vorherRangeLimits, nachherRangeLimits, performanceData.length]);

    const sortDataByKpi = useCallback((data, filter) => {
        if (!data || !data.length) return [];
        
        return [...data].sort((a, b) => {
            const aImp = getImprovement(a, filter);
            const bImp = getImprovement(b, filter);
            return bImp - aImp;
        });
    }, [getImprovement]);

    const sortedData = useMemo(() => {
        if (!performanceData.length) return [];
        return sortDataByKpi(performanceData, kpiFilter);
    }, [performanceData, kpiFilter, sortDataByKpi]);

    // Filter data by search term and vorher range (using debounced values)
    const filteredData = useMemo(() => {
        let filtered = sortedData;

        // Apply search filter
        if (debouncedSearchTerm.trim()) {
            const lowerSearchTerm = debouncedSearchTerm.toLowerCase();
            filtered = filtered.filter(business =>
                business.businessName?.toLowerCase().includes(lowerSearchTerm)
            );
        }

        // Apply all range and activity filters in one pass to reduce iterations
        filtered = filtered.filter(business => {
            const vorherValue = getKpiValue(business, 'beforePeriodData', kpiFilter).total;
            const nachherValue = getKpiValue(business, 'afterPeriodData', kpiFilter).total;

            // Check activity first (cheapest check)
            if (vorherValue === 0 && nachherValue === 0) return false;

            // Check ranges
            if (vorherValue < debouncedVorherRange.min || vorherValue > debouncedVorherRange.max) return false;
            if (nachherValue < debouncedNachherRange.min || nachherValue > debouncedNachherRange.max) return false;

            // Check time range filter
            if (timeRangeFilter === 'vorher') {
                const period = business.beforePeriod;
                if (!period || !period.start || !period.end) return false;
            } else if (timeRangeFilter === 'nachher') {
                const period = business.afterPeriod;
                if (!period || !period.start || !period.end) return false;
            }

            return true;
        });

        return filtered;
    }, [sortedData, debouncedSearchTerm, kpiFilter, debouncedVorherRange, debouncedNachherRange, timeRangeFilter, getKpiValue]);

    const clearCache = async () => {
        setLoading(true);
        setError(null);
        setResult(null);
        const token = localStorage.getItem('auth_token') || localStorage.getItem('token');

        try {
            const response = await fetch('/api/analytics/top-performers-before-after/clear-cache', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            const data = await response.json();
            setResult(data);
            
            if (!response.ok) {
                throw new Error(data.message || 'Failed to clear cache');
            }
            
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const loadPerformanceData = useCallback(async (forceRefresh = false) => {
        setLoading(true);
        setError(null);
        setPerformanceData([]);
        setProgress({ percentage: 0, text: '0%', processed: 0, total: 0 });
        setCurrentPage(1);

        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }
        abortControllerRef.current = new AbortController();

        const token = localStorage.getItem('auth_token') || localStorage.getItem('token');
        if (!token) {
            setError('Authentifizierung erforderlich.');
            setLoading(false);
            return;
        }

        try {
            setLoadingMessage('Verbinde mit API und starte Datenstrom...');
            const cacheKey = forceRefresh ? 'refresh' : 'standard';

            // Check cache if not forcing refresh
            if (!forceRefresh && apiCache.has(cacheKey)) {
                const cachedData = apiCache.get(cacheKey);
                setPerformanceData(cachedData);
                setLoading(false);
                setLoadingMessage('');
                return;
            }

            const refreshParam = forceRefresh ? '?forceRefresh=true' : '';

            const response = await fetch(`/api/analytics/top-performers-before-after${refreshParam}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Accept': 'application/x-ndjson'
                },
                signal: abortControllerRef.current.signal,
            });

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`HTTP ${response.status}: ${errorText}`);
            }

            if (!response.body) {
                throw new Error('ReadableStream nicht unterstützt.');
            }

            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            let buffer = '';
            let collectedData = [];
            let totalExpected = 0;
            let batchData = [];
            const BATCH_SIZE = 10; // Batch updates every 10 items for faster initial render

            setLoadingMessage('Empfange Datenstrom...');

            while (true) {
                const { done, value } = await reader.read();

                if (done) {
                    console.log('✅ Stream completed');
                    // Flush remaining batched data
                    if (batchData.length > 0) {
                        setPerformanceData(prev => [...prev, ...batchData]);
                    }
                    break;
                }

                buffer += decoder.decode(value, { stream: true });
                const lines = buffer.split('\n');
                buffer = lines.pop() || '';

                for (const line of lines) {
                    if (!line.trim()) continue;

                    try {
                        const msg = JSON.parse(line);

                        if (msg.type === 'data' && msg.business) {
                            collectedData.push(msg.business);
                            batchData.push(msg.business);

                            // Batch update every BATCH_SIZE items
                            if (batchData.length >= BATCH_SIZE) {
                                setPerformanceData(prev => [...prev, ...batchData]);
                                batchData = [];
                            }
                        }
                        else if (msg.type === 'progress') {
                            const processed = msg.processed || 0;
                            const total = msg.total || 0;
                            totalExpected = total;
                            
                            setProgress({
                                percentage: total > 0 ? Math.min(90, (processed / total) * 90) : 0,
                                text: `${processed} Unternehmen empfangen`,
                                processed: processed,
                                total: total
                            });
                            setLoadingMessage(`${processed} Unternehmen verarbeitet...`);
                        }
                        else if (msg.type === 'complete') {
                            totalExpected = msg.total || collectedData.length;

                            const cacheStatus = msg.fromCache ? ' (aus Cache geladen)' : '';
                            setProgress({
                                percentage: 100,
                                text: `100% - ${totalExpected} Unternehmen${cacheStatus}`,
                                processed: totalExpected,
                                total: totalExpected
                            });
                            setLoadingMessage(`✅ ${totalExpected} Unternehmen geladen${cacheStatus}`);
                        }
                        else if (msg.type === 'error') {
                            throw new Error(msg.error || 'Unbekannter Fehler');
                        }
                    } catch (parseErr) {
                        console.warn('⚠️ Parse error:', line, parseErr);
                    }
                }
            }

            setPerformanceData(collectedData);
            // Cache the data for next load
            apiCache.set(forceRefresh ? 'refresh' : 'standard', collectedData);
            dataLoadTimeRef.current = Date.now();

            setLoadingMessage(`✅ Fertig! ${collectedData.length} Unternehmen geladen und zwischengespeichert`);

            setTimeout(() => {
                setLoading(false);
            }, 1000); // Reduced from 2000ms

        } catch (err) {
            if (err.name !== 'AbortError') {
                console.error('❌ Error:', err);
                setError(err.message);
            }
        } finally {
            setLoading(false);
        }
    }, []);

    const handleKpiFilterChange = useCallback((event, newValue) => {
        if (newValue) {
            setKpiFilter(newValue);
            setCurrentPage(1);
        }
    }, []);

    const handleVorherRangeChange = useCallback((event, newValue) => {
        setVorherRange({ min: newValue[0], max: newValue[1] });
        setMinInputValue(String(newValue[0]));
        setMaxInputValue(String(newValue[1]));
        setCurrentPage(1);
    }, []);

    const handleNachherRangeChange = useCallback((event, newValue) => {
        setNachherRange({ min: newValue[0], max: newValue[1] });
        setNachherMinInputValue(String(newValue[0]));
        setNachherMaxInputValue(String(newValue[1]));
        setCurrentPage(1);
    }, []);

    // Fixed min input handler
    const handleMinInputChange = useCallback((event) => {
        const value = event.target.value;
        setMinInputValue(value);
        
        // Only update the range if the value is a valid number
        if (value !== '' && !isNaN(Number(value))) {
            const numericValue = Number(value);
            if (numericValue >= vorherRangeLimits.min && numericValue <= vorherRange.max) {
                setVorherRange(prev => ({
                    min: numericValue,
                    max: prev.max
                }));
            }
        }
        setCurrentPage(1);
    }, [vorherRangeLimits.min, vorherRangeLimits.max, vorherRange.max]);

    // Fixed max input handler
    const handleMaxInputChange = useCallback((event) => {
        const value = event.target.value;
        setMaxInputValue(value);
        
        // Only update the range if the value is a valid number
        if (value !== '' && !isNaN(Number(value))) {
            const numericValue = Number(value);
            if (numericValue >= vorherRange.min && numericValue <= vorherRangeLimits.max) {
                setVorherRange(prev => ({
                    min: prev.min,
                    max: numericValue
                }));
            }
        }
        setCurrentPage(1);
    }, [vorherRange.min, vorherRangeLimits.max]);

    // Nachher min input handler
    const handleNachherMinInputChange = useCallback((event) => {
        const value = event.target.value;
        setNachherMinInputValue(value);
        
        // Only update the range if the value is a valid number
        if (value !== '' && !isNaN(Number(value))) {
            const numericValue = Number(value);
            if (numericValue >= nachherRangeLimits.min && numericValue <= nachherRange.max) {
                setNachherRange(prev => ({
                    min: numericValue,
                    max: prev.max
                }));
            }
        }
        setCurrentPage(1);
    }, [nachherRangeLimits.min, nachherRangeLimits.max, nachherRange.max]);

    // Nachher max input handler
    const handleNachherMaxInputChange = useCallback((event) => {
        const value = event.target.value;
        setNachherMaxInputValue(value);
        
        // Only update the range if the value is a valid number
        if (value !== '' && !isNaN(Number(value))) {
            const numericValue = Number(value);
            if (numericValue >= nachherRange.min && numericValue <= nachherRangeLimits.max) {
                setNachherRange(prev => ({
                    min: prev.min,
                    max: numericValue
                }));
            }
        }
        setCurrentPage(1);
    }, [nachherRange.min, nachherRangeLimits.max]);

    // Handle blur events to validate and constrain values
    const handleMinInputBlur = useCallback(() => {
        if (minInputValue === '') {
            setMinInputValue(String(vorherRangeLimits.min));
            setVorherRange(prev => ({
                min: vorherRangeLimits.min,
                max: prev.max
            }));
            return;
        }
        
        const numericValue = Number(minInputValue);
        if (isNaN(numericValue)) {
            setMinInputValue(String(vorherRange.min));
            return;
        }
        
        // Constrain to valid range
        const constrainedValue = Math.max(vorherRangeLimits.min, Math.min(numericValue, vorherRange.max));
        setMinInputValue(String(constrainedValue));
        setVorherRange(prev => ({
            min: constrainedValue,
            max: prev.max
        }));
    }, [minInputValue, vorherRangeLimits.min, vorherRange.min, vorherRange.max]);

    const handleMaxInputBlur = useCallback(() => {
        if (maxInputValue === '') {
            setMaxInputValue(String(vorherRangeLimits.max));
            setVorherRange(prev => ({
                min: prev.min,
                max: vorherRangeLimits.max
            }));
            return;
        }
        
        const numericValue = Number(maxInputValue);
        if (isNaN(numericValue)) {
            setMaxInputValue(String(vorherRange.max));
            return;
        }
        
        // Constrain to valid range
        const constrainedValue = Math.min(vorherRangeLimits.max, Math.max(numericValue, vorherRange.min));
        setMaxInputValue(String(constrainedValue));
        setVorherRange(prev => ({
            min: prev.min,
            max: constrainedValue
        }));
    }, [maxInputValue, vorherRangeLimits.max, vorherRange.min, vorherRange.max]);

    // Handle blur events for nachher inputs
    const handleNachherMinInputBlur = useCallback(() => {
        if (nachherMinInputValue === '') {
            setNachherMinInputValue(String(nachherRangeLimits.min));
            setNachherRange(prev => ({
                min: nachherRangeLimits.min,
                max: prev.max
            }));
            return;
        }
        
        const numericValue = Number(nachherMinInputValue);
        if (isNaN(numericValue)) {
            setNachherMinInputValue(String(nachherRange.min));
            return;
        }
        
        // Constrain to valid range
        const constrainedValue = Math.max(nachherRangeLimits.min, Math.min(numericValue, nachherRange.max));
        setNachherMinInputValue(String(constrainedValue));
        setNachherRange(prev => ({
            min: constrainedValue,
            max: prev.max
        }));
    }, [nachherMinInputValue, nachherRangeLimits.min, nachherRange.min, nachherRange.max]);

    const handleNachherMaxInputBlur = useCallback(() => {
        if (nachherMaxInputValue === '') {
            setNachherMaxInputValue(String(nachherRangeLimits.max));
            setNachherRange(prev => ({
                min: prev.min,
                max: nachherRangeLimits.max
            }));
            return;
        }
        
        const numericValue = Number(nachherMaxInputValue);
        if (isNaN(numericValue)) {
            setNachherMaxInputValue(String(nachherRange.max));
            return;
        }
        
        // Constrain to valid range
        const constrainedValue = Math.min(nachherRangeLimits.max, Math.max(numericValue, nachherRange.min));
        setNachherMaxInputValue(String(constrainedValue));
        setNachherRange(prev => ({
            min: prev.min,
            max: constrainedValue
        }));
    }, [nachherMaxInputValue, nachherRangeLimits.max, nachherRange.min, nachherRange.max]);

    // Handle navigation toggle change
    const handleNavigationToggleChange = useCallback((event, newValue) => {
        if (newValue) {
            setNavigationTarget(newValue);
        }
    }, []);

    // Handle time range toggle change
    const handleTimeRangeToggleChange = useCallback((event, newValue) => {
        if (newValue) {
            setTimeRangeFilter(newValue);
            setCurrentPage(1);
        }
    }, []);

    // Handle opening activity modal
    const handleOpenActivityModal = async (e, business) => {
        e.stopPropagation(); // Prevent card click
        setSelectedBusiness(business);
        setModalOpen(true);
        setModalLoading(true);

        try {
            const token = localStorage.getItem('auth_token');

            // Fetch both keyword performance and activity summary
            const [keywordRes, activityRes] = await Promise.all([
                fetch(
                    `/api/analytics/business-keyword-performance?businessName=${encodeURIComponent(business.businessName)}&months=0`,
                    { headers: { 'Authorization': `Bearer ${token}` } }
                ),
                fetch(
                    `/api/analytics/business-activity-summary?businessName=${encodeURIComponent(business.businessName)}&months=0`,
                    { headers: { 'Authorization': `Bearer ${token}` } }
                )
            ]);

            if (keywordRes.ok) {
                const keywordData = await keywordRes.json();
                const activityData = activityRes.ok ? await activityRes.json() : null;

                setBusinessDetails({
                    ...keywordData,
                    createdAt: activityData?.createdAt || null,
                    activity: activityData?.activity || {}
                });
            } else {
                console.error('Failed to fetch business details');
            }
        } catch (err) {
            console.error('Error fetching business details:', err);
        } finally {
            setModalLoading(false);
        }
    };

    // Handle closing activity modal
    const handleCloseActivityModal = () => {
        setModalOpen(false);
        setSelectedBusiness(null);
        setBusinessDetails(null);
    };

    // Handle opening activity detail modal
    const handleOpenActivityDetail = async (activityType) => {
        let dates = [];
        let title = '';

        switch (activityType) {
            case 'posts':
                dates = (businessDetails?.activity?.posts || []).map(p => ({ date: new Date(p.createdAt), label: new Date(p.createdAt).toLocaleDateString('de-DE') }));
                title = 'Posts Veröffentlicht';
                break;
            case 'services_added':
                dates = (businessDetails?.activity?.services?.added || []).map(s => ({ date: new Date(s.createdAt), label: new Date(s.createdAt).toLocaleDateString('de-DE') }));
                title = 'Hinzugefügte Services';
                break;
            case 'services_updated':
                dates = (businessDetails?.activity?.services?.updated || []).map(s => ({ date: new Date(s.updatedAt), label: new Date(s.updatedAt).toLocaleDateString('de-DE') }));
                title = 'Aktualisierte Service-Beschreibungen';
                break;
            case 'keywords':
                dates = (businessDetails?.activity?.keywords || []).map(k => ({ date: new Date(k.createdAt), label: new Date(k.createdAt).toLocaleDateString('de-DE') }));
                title = 'Hinzugefügte Keywords';
                break;
            case 'media':
                // Use pre-filtered media data from backend (already filtered by business creation date)
                const allMediaItems = businessDetails?.activity?.media || [];

                // Group media by date
                const dateMap = new Map();
                allMediaItems.forEach(m => {
                    const mediaDate = new Date(m.createdAt);
                    const dateStr = mediaDate.toLocaleDateString('de-DE');
                    if (!dateMap.has(dateStr)) {
                        dateMap.set(dateStr, { date: mediaDate, count: 0 });
                    }
                    dateMap.get(dateStr).count += 1;
                });

                // Convert to array format, sorted newest first
                dates = Array.from(dateMap.values())
                    .map(item => ({
                        date: item.date,
                        label: `${item.date.toLocaleDateString('de-DE')} (${item.count}x)`
                    }))
                    .sort((a, b) => b.date - a.date);

                title = 'Media hinzugefügt';
                break;
            default:
                break;
        }

        // Sort dates chronologically (newest first)
        dates.sort((a, b) => b.date - a.date);

        setSelectedActivityType(title);
        setActivityDates(dates);
        setActivityDetailOpen(true);
    };

    const handleCloseActivityDetail = () => {
        setActivityDetailOpen(false);
        setSelectedActivityType(null);
        setActivityDates([]);
    };

    // Pagination (memoized to avoid recalculation)
    const { paginatedData, totalPages } = useMemo(() => {
        const startIndex = (currentPage - 1) * itemsPerPage;
        return {
            paginatedData: filteredData.slice(startIndex, startIndex + itemsPerPage),
            totalPages: Math.ceil(filteredData.length / itemsPerPage)
        };
    }, [filteredData, currentPage, itemsPerPage]);

    // Stats calculation (only use first 100 items for faster stats)
    const stats = useMemo(() => {
        if (!performanceData.length) return null;

        const sampleData = performanceData.slice(0, 100);
        const improvements = sampleData.map(b => getImprovement(b, kpiFilter)).filter(i => !isNaN(i));
        const totalMetric = sampleData.reduce((sum, b) => {
            return sum + getKpiValue(b, 'afterPeriodData', kpiFilter).total;
        }, 0);

        return {
            total: performanceData.length,
            avgImprovement: improvements.length > 0 ? Math.round(improvements.reduce((a, b) => a + b) / improvements.length) : 0,
            totalMetric,
            topPerformers: improvements.filter(i => i > 0).length,
        };
    }, [performanceData.length, kpiFilter, getImprovement, getKpiValue]);

    useEffect(() => {
        loadPerformanceData();
        
        return () => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };
    }, [loadPerformanceData]);

    const currentKpi = kpiOptions.find(o => o.value === kpiFilter);

    return (
        <Box sx={{ p: '30px 34px', backgroundColor: '#F7F6F3', minHeight: '100vh' }}>
            {/* Header Section */}
            <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box>
                    <Typography
                        sx={{
                            fontSize: '12px',
                            fontWeight: 700,
                            color: '#C80F2E',
                            textTransform: 'uppercase',
                            letterSpacing: '0.08em',
                            fontFamily: 'Plus Jakarta Sans, system-ui',
                            mb: 1
                        }}
                    >
                        Erfolge
                    </Typography>
                    <Typography
                        sx={{
                            fontSize: '30px',
                            fontWeight: 800,
                            color: '#0A0A0B',
                            fontFamily: 'Plus Jakarta Sans, system-ui',
                            letterSpacing: '-0.025em',
                            mb: 0.5
                        }}
                    >
                        Top Performer
                    </Typography>
                    <Typography
                        sx={{
                            fontSize: '14px',
                            color: '#55555D',
                            fontFamily: 'Plus Jakarta Sans, system-ui'
                        }}
                    >
                        Größte Sichtbarkeits-Sprünge seit Betreuungsbeginn
                    </Typography>
                </Box>

                {/* Metric Toggle - Top Right */}
                <ToggleButtonGroup
                    value={kpiFilter}
                    exclusive
                    onChange={handleKpiFilterChange}
                    disabled={loading}
                    sx={{
                        backgroundColor: '#F8F7F9',
                        borderRadius: '8px',
                        border: '1px solid #E0E0E0',
                        padding: '4px',
                        '& .MuiToggleButton-root': {
                            textTransform: 'none',
                            fontWeight: 500,
                            fontSize: '13px',
                            fontFamily: 'Plus Jakarta Sans, system-ui',
                            color: '#55555D',
                            border: 'none',
                            padding: '8px 16px',
                            borderRadius: '6px',
                            '&.Mui-selected': {
                                backgroundColor: '#FFFFFF',
                                color: '#0A0A0B',
                                fontWeight: 600,
                                border: '1px solid #ECEBEE'
                            }
                        }
                    }}
                >
                    {kpiOptions.map(opt => (
                        <ToggleButton key={opt.value} value={opt.value}>
                            {opt.label}
                        </ToggleButton>
                    ))}
                </ToggleButtonGroup>
            </Box>

            {/* Stats */}
            {stats && !loading && (
                <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                    gap: '16px',
                    mb: 3
                }}>
                    <Card sx={{
                        background: '#FFFFFF',
                        border: '1px solid #ECEBEE',
                        borderRadius: '14px',
                        padding: '20px 18px',
                        boxShadow: '0 1px 2px rgba(16,16,20,.06), 0 2px 6px rgba(16,16,20,.05)',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '12px'
                    }}>
                        <Typography sx={{
                            fontSize: '13px',
                            fontWeight: 600,
                            color: '#55555D',
                            fontFamily: 'Plus Jakarta Sans, system-ui'
                        }}>
                            Unternehmen
                        </Typography>
                        <Typography sx={{
                            fontSize: '40px',
                            fontWeight: 700,
                            color: '#0A0A0B',
                            fontFamily: 'JetBrains Mono, monospace',
                            lineHeight: 1.1,
                            margin: 0
                        }}>
                            {stats.total}
                        </Typography>
                    </Card>

                    <Card sx={{
                        background: '#E9F7EF',
                        border: '1px solid #B8E5D8',
                        borderRadius: '14px',
                        padding: '20px 18px',
                        boxShadow: '0 1px 2px rgba(16,16,20,.06), 0 2px 6px rgba(16,16,20,.05)',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '12px'
                    }}>
                        <Typography sx={{
                            fontSize: '13px',
                            fontWeight: 600,
                            color: '#1E9E5A',
                            fontFamily: 'Plus Jakarta Sans, system-ui'
                        }}>
                            Ø Verbesserung
                        </Typography>
                        <Typography sx={{
                            fontSize: '40px',
                            fontWeight: 700,
                            color: '#1E9E5A',
                            fontFamily: 'JetBrains Mono, monospace',
                            lineHeight: 1.1,
                            margin: 0
                        }}>
                            +{stats.avgImprovement}%
                        </Typography>
                    </Card>

                    <Card sx={{
                        background: '#FFFFFF',
                        border: '1px solid #ECEBEE',
                        borderRadius: '14px',
                        padding: '20px 18px',
                        boxShadow: '0 1px 2px rgba(16,16,20,.06), 0 2px 6px rgba(16,16,20,.05)',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '12px'
                    }}>
                        <Typography sx={{
                            fontSize: '13px',
                            fontWeight: 600,
                            color: '#55555D',
                            fontFamily: 'Plus Jakarta Sans, system-ui'
                        }}>
                            Gesamt {currentKpi?.label}
                        </Typography>
                        <Typography sx={{
                            fontSize: '40px',
                            fontWeight: 700,
                            color: '#0A0A0B',
                            fontFamily: 'JetBrains Mono, monospace',
                            lineHeight: 1.1,
                            margin: 0
                        }}>
                            {formatKpiValue(stats.totalMetric)}
                        </Typography>
                    </Card>

                    <Card sx={{
                        background: '#FFFFFF',
                        border: '1px solid #ECEBEE',
                        borderRadius: '14px',
                        padding: '20px 18px',
                        boxShadow: '0 1px 2px rgba(16,16,20,.06), 0 2px 6px rgba(16,16,20,.05)',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '12px'
                    }}>
                        <Typography sx={{
                            fontSize: '13px',
                            fontWeight: 600,
                            color: '#55555D',
                            fontFamily: 'Plus Jakarta Sans, system-ui'
                        }}>
                            Top Performer
                        </Typography>
                        <Typography sx={{
                            fontSize: '40px',
                            fontWeight: 700,
                            color: '#0A0A0B',
                            fontFamily: 'JetBrains Mono, monospace',
                            lineHeight: 1.1,
                            margin: 0
                        }}>
                            {stats.topPerformers}
                        </Typography>
                    </Card>
                </Box>
            )}

            {/* Loading */}
            {loading && (
                <Card sx={{ mb: 3 }}>
                    <CardContent sx={{ textAlign: 'center', py: 4 }}>
                        <CircularProgress size={60} />
                        <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>{loadingMessage}</Typography>
                        <Box sx={{ width: '100%', maxWidth: 400, mx: 'auto', mt: 3 }}>
                            <LinearProgress
                                variant="determinate"
                                value={Math.min(progress.percentage, 100)}
                                sx={{ height: 8, borderRadius: 4 }}
                            />
                        </Box>
                        <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
                            {progress.text}
                        </Typography>
                        {progress.total > 0 && (
                            <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary' }}>
                                {progress.processed} von {progress.total} Unternehmen empfangen
                            </Typography>
                        )}
                        {performanceData.length > 0 && (
                            <Typography variant="caption" sx={{ display: 'block', mt: 1, fontWeight: 'bold', color: '#4caf50' }}>
                                ✅ {performanceData.length} Unternehmen im Speicher
                            </Typography>
                        )}
                    </CardContent>
                </Card>
            )}

            {/* Error */}
            {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                </Alert>
            )}

            

            {/* Data Grid */}
            {!loading && paginatedData.length > 0 && (
                <>
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                        {paginatedData.map((business, idx) => {
                            const improvement = getImprovement(business, kpiFilter);
                            const beforeMetrics = getKpiValue(business, 'beforePeriodData', kpiFilter);
                            const afterMetrics = getKpiValue(business, 'afterPeriodData', kpiFilter);
                            const rankIdx = (currentPage - 1) * itemsPerPage + idx;

                            return (
                                <Grid item xs={12} md={6} lg={4} key={business.googleLocationId || `business-${rankIdx}`}>
                                    <Card
                                        sx={{
                                            height: '100%',
                                            background: '#FFFFFF',
                                            border: '1px solid #ECEBEE',
                                            borderRadius: '14px',
                                            padding: '20px 18px',
                                            boxShadow: '0 1px 2px rgba(16,16,20,.06), 0 2px 6px rgba(16,16,20,.05)',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: '12px',
                                            transition: 'all 120ms ease',
                                            cursor: 'pointer',
                                            position: 'relative',
                                            overflow: 'visible',
                                            '&:hover': {
                                                transform: 'translateY(-2px)',
                                                boxShadow: '0 2px 4px rgba(16,16,20,.05), 0 6px 16px rgba(16,16,20,.08)'
                                            }
                                        }}
                                        onClick={async () => {
                                          

                                            if (navigationTarget === 'performance-details') {
                                                try {
                                                    
                                                    const selectedPeriod = timeRangeFilter === 'vorher'
                                                        ? business.beforePeriod
                                                        : business.afterPeriod;


                                                    navigate('/performance-details', {
                                                        state: {
                                                            business,
                                                            selectedTimePeriod: selectedPeriod,
                                                            timeRangeFilter: timeRangeFilter,
                                                            
                                                        }
                                                    });
                                                } catch (error) {
                                                    console.error('❌ Error fetching fresh data:', error);
                                                    // Fallback: use cached data
                                                    const selectedPeriod = timeRangeFilter === 'vorher'
                                                        ? business.beforePeriod
                                                        : business.afterPeriod;

                                                    navigate('/performance-details', {
                                                        state: {
                                                            business,
                                                            selectedTimePeriod: selectedPeriod,
                                                            timeRangeFilter: timeRangeFilter
                                                        }
                                                    });
                                                }
                                            } else {
                                                // Navigate to activity page
                                                const route = `/activity/${business.googleLocationId}`;
                                                navigate(route, {
                                                    state: {
                                                        business,
                                                        selectedTimePeriod: timeRangeFilter === 'vorher'
                                                            ? business.beforePeriod
                                                            : business.afterPeriod,
                                                        timeRangeFilter: timeRangeFilter
                                                    }
                                                });
                                            }
                                        }}
                                    >
                                    {/* Header with Rank & Info */}
                                    <Box display="flex" justifyContent="space-between" alignItems="center">
                                        <Box display="flex" alignItems="center" gap={2}>
            {/* Rank Badge */}
            <Box
                sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    background: rankIdx === 0 ? '#FFD700' : rankIdx === 1 ? '#C0C0C0' : rankIdx === 2 ? '#CD7F32' : '#9E9E9E',
                    color: rankIdx === 0 ? '#000' : '#fff',
                    fontWeight: 700,
                    fontSize: '16px',
                    fontFamily: 'Plus Jakarta Sans, system-ui',
                    flexShrink: 0
                }}
            >
                #{rankIdx + 1}
            </Box>

            {/* Improvement Chip */}
            <Chip
                label={`↗ +${improvement}%`}
                size="small"
                sx={{
                    backgroundColor: '#D7EEE5',
                    color: '#1F7A5F',
                    fontWeight: 700,
                    fontSize: '11px',
                    height: '22px',
                    borderRadius: '999px',
                    fontFamily: 'Plus Jakarta Sans, system-ui'
                }}
            />
        </Box>

        <Box display="flex" alignItems="center" gap={1}>
            {/* Days Count */}
            <Typography sx={{ fontSize: '12px', color: '#55555D', fontFamily: 'Plus Jakarta Sans, system-ui' }}>
                {business.daysSinceCreation || '0'} Tage
            </Typography>

            {/* Eye Button */}
            <IconButton
                size="small"
                onClick={(e) => handleOpenActivityModal(e, business)}
                sx={{
                    color: '#888',
                    '&:hover': { color: '#000' }
                }}
                title="Activity anzeigen"
            >
                <Visibility sx={{ fontSize: '18px' }} />
            </IconButton>
        </Box>
    </Box>

    {/* Business Name */}
    <Typography
        sx={{
            fontSize: '14px',
            fontWeight: 700,
            color: '#0A0A0B',
            mb: 2,
            fontFamily: 'Plus Jakarta Sans, system-ui',
            minHeight: '40px',
            display: 'flex',
            alignItems: 'flex-start'
        }}
    >
        {business.businessName || 'Unbekannt'}
    </Typography>

    {/* KPI Comparison */}
    <Box
        sx={{
            background: '#F8F7F9',
            borderRadius: '14px',
            p: 2,
            display: 'grid',
            gridTemplateColumns: '1fr auto 1fr',
            alignItems: 'center',
            gap: 1,
            textAlign: 'center'
        }}
    >
        {/* Before */}
        <Box>
            <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#55555D', mb: 0.5, textTransform: 'uppercase', fontFamily: 'Plus Jakarta Sans, system-ui', letterSpacing: '0.04em' }}>
                Vorher
            </Typography>
            <Typography sx={{ fontSize: '18px', fontWeight: 700, color: '#0A0A0B', fontFamily: 'Plus Jakarta Sans, system-ui' }}>
                {formatKpiValue(beforeMetrics.total)}
            </Typography>
        </Box>

        {/* Arrow */}
        <Typography sx={{ color: '#1E9E5A', fontSize: '20px', fontWeight: 600 }}>
            →
        </Typography>

        {/* After */}
        <Box>
            <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#1E9E5A', mb: 0.5, textTransform: 'uppercase', fontFamily: 'Plus Jakarta Sans, system-ui', letterSpacing: '0.04em' }}>
                Nachher
            </Typography>
            <Typography sx={{ fontSize: '18px', fontWeight: 700, color: '#1E9E5A', fontFamily: 'Plus Jakarta Sans, system-ui' }}>
                {formatKpiValue(afterMetrics.total)}
            </Typography>
        </Box>
    </Box>
                                    </Card>
                                </Grid>
                            );
                        })}
                    </Grid>

                    {/* Pagination */}
                    {totalPages > 1 && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4, mb: 2 }}>
                            <Pagination
                                count={totalPages}
                                page={currentPage}
                                onChange={(e, v) => setCurrentPage(v)}
                                sx={{
                                    '& .MuiPaginationItem-root': {
                                        fontFamily: 'Plus Jakarta Sans, system-ui',
                                        fontSize: '13px'
                                    },
                                    '& .Mui-selected': {
                                        backgroundColor: '#C80F2E',
                                        color: '#FFFFFF',
                                        '&:hover': {
                                            backgroundColor: '#AE0B27'
                                        }
                                    }
                                }}
                            />
                        </Box>
                    )}
                </>
            )}

            {/* Empty State */}
            {!loading && performanceData.length === 0 && !error && (
                <Card>
                    <CardContent sx={{ textAlign: 'center', py: 4 }}>
                        <Analytics sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                        <Typography variant="h6">Keine Daten</Typography>
                        <Typography variant="body2" color="text.secondary">
                            Klicken Sie auf "Aktualisieren", um Daten zu laden.
                        </Typography>
                    </CardContent>
                </Card>
            )}

            {/* Activity Modal */}
            <Dialog
                open={modalOpen}
                onClose={handleCloseActivityModal}
                maxWidth={false}
                fullWidth
                PaperProps={{
                    sx: {
                        background: '#1a1a1b',
                        color: '#FFFFFF',
                        width: '90vw',
                        height: '90vh',
                        maxWidth: 'none',
                        maxHeight: 'none',
                        margin: 'auto'
                    }
                }}
            >
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexDirection: 'column', gap: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                        <Typography sx={{ fontWeight: 700,color: '#FFFFFF', fontSize: '16px' }}>
                            {selectedBusiness?.businessName}
                        </Typography>
                        <IconButton size="small" onClick={handleCloseActivityModal} sx={{ color: '#FFFFFF' }}>
                            <CloseIcon />
                        </IconButton>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 3, alignItems: 'center' }}>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <Typography sx={{ fontSize: '12px', color: '#888', fontFamily: 'JetBrains Mono, monospace' }}>
                                Location ID:
                            </Typography>
                            <Typography sx={{ fontSize: '12px', color: '#FFA500', fontFamily: 'JetBrains Mono, monospace', fontWeight: 600 }}>
                                {selectedBusiness?.googleLocationId}
                            </Typography>
                        </Box>
                        <Box sx={{ width: '1px', height: '16px', background: '#3a3a3b' }} />
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <Typography sx={{ fontSize: '12px', color: '#888', fontFamily: 'JetBrains Mono, monospace' }}>
                                Erstellt:
                            </Typography>
                            <Typography sx={{ fontSize: '12px', color: '#1E9E5A', fontFamily: 'JetBrains Mono, monospace', fontWeight: 600 }}>
                                {businessDetails?.createdAt
                                    ? new Date(businessDetails.createdAt).toLocaleDateString('de-DE')
                                    : selectedBusiness?.createdAt
                                    ? new Date(selectedBusiness.createdAt).toLocaleDateString('de-DE')
                                    : 'N/A'}
                            </Typography>
                        </Box>
                    </Box>
                </DialogTitle>




                <DialogContent sx={{ pt: 3, px: 4, height: 'calc(100vh - 100px)', overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
                  {modalLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                      <CircularProgress />
                    </Box>
                  ) : businessDetails?.keywords ? (
                        <Box sx={{ mt: 0 }}>
                          {/* Performance Overview Section */}
                          <Box sx={{ mb: 4, p: 2.5, background: 'linear-gradient(135deg, #1E9E5A15 0%, #1E9E5A08 100%)', border: '1px solid #1E9E5A30', borderRadius: '12px' }}>
                            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 3 }}>
                              <Box>
                                <Typography sx={{ fontSize: '11px', fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: '0.05em', mb: 1 }}>
                                  Durchschnittliche Verbesserung
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                                  <Typography sx={{ fontSize: '36px', fontWeight: 700, color: '#1E9E5A' }}>
                                    +{businessDetails.avgImprovement || 0}%
                                  </Typography>
                                  <Typography sx={{ fontSize: '13px', color: '#888' }}>
                                    über {businessDetails.keywordCount || 0} Keywords
                                  </Typography>
                                </Box>
                              </Box>
                              <Box>
                                <Typography sx={{ fontSize: '11px', fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: '0.05em', mb: 1 }}>
                                  Keywords mit Fortschritt
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                                  <Typography sx={{ fontSize: '36px', fontWeight: 700, color: '#1E9E5A' }}>
                                    {businessDetails.keywords.filter(k => k.improvement > 0).length}
                                  </Typography>
                                  <Typography sx={{ fontSize: '13px', color: '#888' }}>
                                    von {businessDetails.keywordCount || 0} Keywords
                                  </Typography>
                                </Box>
                              </Box>
                            </Box>
                          </Box>

                          {/* Activity Timeline Section */}
                          {businessDetails.activity && (
                            <Box sx={{ mb: 4 }}>
                              <Box sx={{ mb: 2.5 }}>
                                <Typography sx={{ fontSize: '12px', fontWeight: 700, color: '#FFFFFF', textTransform: 'uppercase', letterSpacing: '0.05em', mb: 0.5 }}>
                                  Aktivität seit Erstellung
                                </Typography>
                                <Typography sx={{ fontSize: '11px', color: '#888' }}>
                                  Maßnahmen die zur Leistung beitragen
                                </Typography>
                              </Box>

                              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(5, 1fr)' }, gap: 1.5 }}>
                                {/* Posts */}
                                <Box sx={{
                                  background: businessDetails.activity.postsAdded > 0 ? '#1a3a2e' : '#2a2a2b',
                                  border: businessDetails.activity.postsAdded > 0 ? '1px solid #1E9E5A40' : '1px solid #3a3a3b',
                                  p: 2,
                                  borderRadius: '10px',
                                  display: 'flex',
                                  flexDirection: 'column',
                                  justifyContent: 'space-between',
                                  minHeight: '110px',
                                  transition: 'all 200ms ease',
                                  '&:hover': {
                                    background: businessDetails.activity.postsAdded > 0 ? '#1a4a36' : '#323232',
                                    borderColor: businessDetails.activity.postsAdded > 0 ? '#1E9E5A60' : '#4a4a4a'
                                  }
                                }}>
                                  <Box>
                                    <Typography sx={{ fontSize: '10px', fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: '0.04em', mb: 1 }}>
                                      Beiträge
                                    </Typography>
                                    <Typography sx={{ fontSize: '28px', fontWeight: 700, color: businessDetails.activity.postsAdded > 0 ? '#1E9E5A' : '#555', mb: 0.5 }}>
                                      {businessDetails.activity.postsAdded || 0}
                                    </Typography>
                                  </Box>
                                  {businessDetails.activity.postsAdded > 0 && (
                                    <IconButton size="small" onClick={() => handleOpenActivityDetail('posts')} sx={{ color: '#1E9E5A', alignSelf: 'flex-start', p: 0, fontSize: '14px', '&:hover': { color: '#FFFFFF' } }}>
                                      <Visibility sx={{ fontSize: '16px' }} />
                                    </IconButton>
                                  )}
                                </Box>

                                {/* Services Added */}
                                <Box sx={{
                                  background: businessDetails.activity.servicesAdded > 0 ? '#1a3a2e' : '#2a2a2b',
                                  border: businessDetails.activity.servicesAdded > 0 ? '1px solid #1E9E5A40' : '1px solid #3a3a3b',
                                  p: 2,
                                  borderRadius: '10px',
                                  display: 'flex',
                                  flexDirection: 'column',
                                  justifyContent: 'space-between',
                                  minHeight: '110px',
                                  transition: 'all 200ms ease',
                                  '&:hover': {
                                    background: businessDetails.activity.servicesAdded > 0 ? '#1a4a36' : '#323232',
                                    borderColor: businessDetails.activity.servicesAdded > 0 ? '#1E9E5A60' : '#4a4a4a'
                                  }
                                }}>
                                  <Box>
                                    <Typography sx={{ fontSize: '10px', fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: '0.04em', mb: 1 }}>
                                      Services
                                    </Typography>
                                    <Typography sx={{ fontSize: '28px', fontWeight: 700, color: businessDetails.activity.servicesAdded > 0 ? '#1E9E5A' : '#555', mb: 0.5 }}>
                                      {businessDetails.activity.servicesAdded || 0}
                                    </Typography>
                                  </Box>
                                  {businessDetails.activity.servicesAdded > 0 && (
                                    <IconButton size="small" onClick={() => handleOpenActivityDetail('services_added')} sx={{ color: '#1E9E5A', alignSelf: 'flex-start', p: 0, fontSize: '14px', '&:hover': { color: '#FFFFFF' } }}>
                                      <Visibility sx={{ fontSize: '16px' }} />
                                    </IconButton>
                                  )}
                                </Box>

                                {/* Media */}
                                <Box sx={{
                                  background: businessDetails.activity.mediaAdded > 0 ? '#1a3a2e' : '#2a2a2b',
                                  border: businessDetails.activity.mediaAdded > 0 ? '1px solid #1E9E5A40' : '1px solid #3a3a3b',
                                  p: 2,
                                  borderRadius: '10px',
                                  display: 'flex',
                                  flexDirection: 'column',
                                  justifyContent: 'space-between',
                                  minHeight: '110px',
                                  transition: 'all 200ms ease',
                                  '&:hover': {
                                    background: businessDetails.activity.mediaAdded > 0 ? '#1a4a36' : '#323232',
                                    borderColor: businessDetails.activity.mediaAdded > 0 ? '#1E9E5A60' : '#4a4a4a'
                                  }
                                }}>
                                  <Box>
                                    <Typography sx={{ fontSize: '10px', fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: '0.04em', mb: 1 }}>
                                      Medien
                                    </Typography>
                                    <Typography sx={{ fontSize: '28px', fontWeight: 700, color: businessDetails.activity.mediaAdded > 0 ? '#1E9E5A' : '#555', mb: 0.5 }}>
                                      {businessDetails.activity.mediaAdded || 0}
                                    </Typography>
                                  </Box>
                                  {businessDetails.activity.mediaAdded > 0 && (
                                    <IconButton size="small" onClick={() => handleOpenActivityDetail('media')} sx={{ color: '#1E9E5A', alignSelf: 'flex-start', p: 0, fontSize: '14px', '&:hover': { color: '#FFFFFF' } }}>
                                      <Visibility sx={{ fontSize: '16px' }} />
                                    </IconButton>
                                  )}
                                </Box>

                                {/* Keywords */}
                                <Box sx={{
                                  background: businessDetails.activity.keywordsAdded > 0 ? '#1a3a2e' : '#2a2a2b',
                                  border: businessDetails.activity.keywordsAdded > 0 ? '1px solid #1E9E5A40' : '1px solid #3a3a3b',
                                  p: 2,
                                  borderRadius: '10px',
                                  display: 'flex',
                                  flexDirection: 'column',
                                  justifyContent: 'space-between',
                                  minHeight: '110px',
                                  transition: 'all 200ms ease',
                                  '&:hover': {
                                    background: businessDetails.activity.keywordsAdded > 0 ? '#1a4a36' : '#323232',
                                    borderColor: businessDetails.activity.keywordsAdded > 0 ? '#1E9E5A60' : '#4a4a4a'
                                  }
                                }}>
                                  <Box>
                                    <Typography sx={{ fontSize: '10px', fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: '0.04em', mb: 1 }}>
                                      Keywords
                                    </Typography>
                                    <Typography sx={{ fontSize: '28px', fontWeight: 700, color: businessDetails.activity.keywordsAdded > 0 ? '#1E9E5A' : '#555', mb: 0.5 }}>
                                      {businessDetails.activity.keywordsAdded || 0}
                                    </Typography>
                                  </Box>
                                  {businessDetails.activity.keywordsAdded > 0 && (
                                    <IconButton size="small" onClick={() => handleOpenActivityDetail('keywords')} sx={{ color: '#1E9E5A', alignSelf: 'flex-start', p: 0, fontSize: '14px', '&:hover': { color: '#FFFFFF' } }}>
                                      <Visibility sx={{ fontSize: '16px' }} />
                                    </IconButton>
                                  )}
                                </Box>

                                {/* Service Updates */}
                                <Box sx={{
                                  background: businessDetails.activity.servicesUpdated > 0 ? '#2a2a2b' : '#1a1a1b',
                                  border: businessDetails.activity.servicesUpdated > 0 ? '1px solid #FFA50040' : '1px solid #3a3a3b',
                                  p: 2,
                                  borderRadius: '10px',
                                  display: 'flex',
                                  flexDirection: 'column',
                                  justifyContent: 'space-between',
                                  minHeight: '110px',
                                  transition: 'all 200ms ease',
                                  '&:hover': {
                                    background: businessDetails.activity.servicesUpdated > 0 ? '#332211' : '#2a2a2b',
                                    borderColor: businessDetails.activity.servicesUpdated > 0 ? '#FFA50060' : '#4a4a4a'
                                  }
                                }}>
                                  <Box>
                                    <Typography sx={{ fontSize: '10px', fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: '0.04em', mb: 1 }}>
                                      Service-Updates
                                    </Typography>
                                    <Typography sx={{ fontSize: '28px', fontWeight: 700, color: businessDetails.activity.servicesUpdated > 0 ? '#FFA500' : '#555', mb: 0.5 }}>
                                      {businessDetails.activity.servicesUpdated || 0}
                                    </Typography>
                                  </Box>
                                  {businessDetails.activity.servicesUpdated > 0 && (
                                    <IconButton size="small" onClick={() => handleOpenActivityDetail('services_updated')} sx={{ color: '#FFA500', alignSelf: 'flex-start', p: 0, fontSize: '14px', '&:hover': { color: '#FFFFFF' } }}>
                                      <Visibility sx={{ fontSize: '16px' }} />
                                    </IconButton>
                                  )}
                                </Box>
                              </Box>
                            </Box>
                          )}


              <Box sx={{ mb: 0 }}>
                <Box sx={{ mb: 3 }}>
                  <Typography sx={{ fontSize: '12px', fontWeight: 700, color: '#FFFFFF', textTransform: 'uppercase', letterSpacing: '0.05em', mb: 0.5 }}>
                    Detaillierte Keyword-Analyse
                  </Typography>
                  <Typography sx={{ fontSize: '11px', color: '#888' }}>
                    Ranking-Entwicklung und Performance-Tracking
                  </Typography>
                </Box>

                {/* Summary Stats - Enhanced */}
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(4, 1fr)' }, gap: 2, mb: 4 }}>
                  <Box sx={{ background: '#1a3a2e', p: 2, borderRadius: '10px', border: '1px solid #1E9E5A30' }}>
                    <Typography sx={{ fontSize: '10px', color: '#888', textTransform: 'uppercase', letterSpacing: '0.04em', mb: 1 }}>Analysen pro Keyword</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                      <Typography sx={{ fontSize: '24px', fontWeight: 700, color: '#1E9E5A' }}>
                        {businessDetails.keywords.length > 0
                          ? (businessDetails.keywords.reduce((sum, k) => sum + (k.snapshotCount || 0), 0) / businessDetails.keywords.length).toFixed(1)
                          : 0}
                      </Typography>
                      <Typography sx={{ fontSize: '11px', color: '#888' }}>durchschnittlich</Typography>
                    </Box>
                  </Box>
                  <Box sx={{ background: '#1a3a2e', p: 2, borderRadius: '10px', border: '1px solid #1E9E5A30' }}>
                    <Typography sx={{ fontSize: '10px', color: '#888', textTransform: 'uppercase', letterSpacing: '0.04em', mb: 1 }}>Verbessert</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                      <Typography sx={{ fontSize: '24px', fontWeight: 700, color: '#1E9E5A' }}>
                        {businessDetails.keywords.filter(k => k.improvement > 0).length}
                      </Typography>
                      <Typography sx={{ fontSize: '11px', color: '#888' }}>/ {businessDetails.keywords.length}</Typography>
                    </Box>
                  </Box>
                  <Box sx={{ background: '#2a2a2b', p: 2, borderRadius: '10px', border: '1px solid #3a3a3b' }}>
                    <Typography sx={{ fontSize: '10px', color: '#888', textTransform: 'uppercase', letterSpacing: '0.04em', mb: 1 }}>Stabil</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                      <Typography sx={{ fontSize: '24px', fontWeight: 700, color: '#888' }}>
                        {businessDetails.keywords.filter(k => k.improvement === 0).length}
                      </Typography>
                      <Typography sx={{ fontSize: '11px', color: '#666' }}>/ {businessDetails.keywords.length}</Typography>
                    </Box>
                  </Box>
                  <Box sx={{ background: '#2a1a1a', p: 2, borderRadius: '10px', border: '1px solid #ef534050' }}>
                    <Typography sx={{ fontSize: '10px', color: '#888', textTransform: 'uppercase', letterSpacing: '0.04em', mb: 1 }}>Rückgang</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                      <Typography sx={{ fontSize: '24px', fontWeight: 700, color: '#ef5350' }}>
                        {businessDetails.keywords.filter(k => k.improvement < 0).length}
                      </Typography>
                      <Typography sx={{ fontSize: '11px', color: '#666' }}>/ {businessDetails.keywords.length}</Typography>
                    </Box>
                  </Box>
                </Box>

                {/* Keywords Grid View - Creative Layout */}
                <Box sx={{ mb: 3 }}>
                  <Typography sx={{ fontSize: '11px', fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: '0.04em', mb: 2 }}>
                    Top Keywords nach Verbesserung
                  </Typography>
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 2 }}>
                    {businessDetails.keywords
                      .filter(k => k.improvement > 0)
                      .sort((a, b) => b.improvement - a.improvement)
                      .slice(0, 6)
                      .map((kw, idx) => {
                        const rankChange = (kw.firstSnapshotAvgRank || 0) - (kw.lastSnapshotAvgRank || 0);
                        const rankPercentage = kw.firstSnapshotAvgRank ? Math.min(100, (rankChange / (kw.firstSnapshotAvgRank || 1)) * 100) : 0;
                        return (
                          <Box
                            key={idx}
                            sx={{
                              background: 'linear-gradient(135deg, #1a3a2e 0%, #1a2a2b 100%)',
                              border: '1px solid #1E9E5A30',
                              borderRadius: '10px',
                              p: 2,
                              transition: 'all 200ms ease',
                              position: 'relative',
                              '&:hover': {
                                border: '1px solid #1E9E5A60',
                                boxShadow: '0 0 12px rgba(30, 158, 90, 0.15)'
                              }
                            }}
                          >

                            {/* Two Column Layout: Stats Left, Graph Right - Full Height */}
                            <Box sx={{ display: 'flex', gap: 2, minHeight: '120px' }}>
                              {/* LEFT SIDE - STATS */}
                              <Box sx={{ flex: '0 0 auto', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', minWidth: '140px' }}>
                                {/* Top - Keyword and Range */}
                                <Box>
                                  <Typography sx={{ fontSize: '12px', fontWeight: 700, color: '#FFFFFF', mb: 0.75, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {kw.keyword}
                                  </Typography>

                                </Box>

                                {/* Stats Column - Best Rank, Count, Duration, Avg Rank */}
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, mb: 1 }}>
                                  {/* Best Rank */}
                                  <Box sx={{ background: '#2a2a2b', p: 0.75, borderRadius: '6px', border: '1px solid #3a3a3b', minWidth: 'fit-content' }}>
                                    <Typography sx={{ fontSize: '7px', color: '#888', mb: 0.25, textTransform: 'uppercase', letterSpacing: '0.02em' }}>Best</Typography>
                                    <Typography sx={{ fontSize: '9px', fontWeight: 700, color: '#9c9c9c' }}>
                                      #{kw.firstSnapshotBestRank || 'N/A'} → #{kw.lastSnapshotBestRank || 'N/A'}
                                    </Typography>
                                  </Box>

                                    {/* Avg Rank */}
                                  <Box sx={{ background: '#2a2a2b', p: 0.75, borderRadius: '6px', border: '1px solid #3a3a3b', minWidth: 'fit-content' }}>
                                    <Typography sx={{ fontSize: '7px', color: '#888', mb: 0.25, textTransform: 'uppercase', letterSpacing: '0.02em' }}>Ø Rang</Typography>
                                    <Typography sx={{ fontSize: '9px', fontWeight: 700, color: '#9c9c9c' }}>
                                      #{kw.firstSnapshotAvgRank || 'N/A'} → #{kw.lastSnapshotAvgRank || 'N/A'}
                                    </Typography>
                                  </Box>

                                  {/* Count and Duration Row */}
                                  <Box sx={{ display: 'flex', gap: 0.75 }}>
                                    {/* Snapshot Count */}
                                    <Box sx={{ background: '#1E9E5A20', border: '1px solid #1E9E5A40', px: 1, py: 0.5, borderRadius: '4px', minWidth: 'fit-content' }}>
                                      <Typography sx={{ fontSize: '9px', fontWeight: 700, color: '#1E9E5A' }}>
                                        {kw.snapshotCount || 0}x
                                      </Typography>
                                    </Box>

                                    {/* Time Span */}
                                    <Box sx={{ background: '#88888820', border: '1px solid #88888840', px: 1, py: 0.5, borderRadius: '4px', minWidth: 'fit-content' }}>
                                      <Typography sx={{ fontSize: '8px', fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: '0.02em' }}>
                                        {kw.firstSnapshotDate && kw.lastSnapshotDate
                                          ? (() => {
                                            const days = Math.ceil((new Date(kw.lastSnapshotDate) - new Date(kw.firstSnapshotDate)) / (1000 * 60 * 60 * 24));
                                            const months = Math.floor(days / 30);
                                            const remainingDays = days % 30;
                                            return months > 0 ? `${months}M ${remainingDays}d` : `${days}d`;
                                          })()
                                          : 'N/A'}
                                      </Typography>
                                    </Box>
                                  </Box>


                                </Box>

                                {/* Bottom - Progress Bar Full Width */}
                                <Box sx={{ width: '100%' }}>
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5, alignItems: 'center' }}>
                                    <Typography sx={{ fontSize: '8px', color: '#888', textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                                      Verb.
                                    </Typography>
                                    <Typography sx={{ fontSize: '10px', fontWeight: 700, color: '#1E9E5A' }}>
                                      +{kw.improvement}
                                    </Typography>
                                  </Box>
                                  <Box sx={{ background: '#2a2a2b', height: '5px', borderRadius: '3px', overflow: 'hidden', border: '1px solid #3a3a3b' }}>
                                    <Box sx={{
                                      height: '100%',
                                      background: 'linear-gradient(90deg, #1E9E5A 0%, #1E9E5A80 100%)',
                                      width: `${Math.min(100, (kw.improvement / 20) * 100)}%`,
                                      transition: 'width 500ms ease-out'
                                    }} />
                                  </Box>
                                </Box>
                              </Box>

                              {/* RIGHT SIDE - INTERACTIVE GRAPH FULL HEIGHT */}
                              {kw.trend && kw.trend.length > 0 && (() => {
                                const trendData = kw.trend.sort((a, b) => new Date(a.date) - new Date(b.date));
                                const minRank = Math.min(...trendData.map(t => t.rank || 100));
                                const maxRank = Math.max(...trendData.map(t => t.rank || 1));

                                // Chart dimensions - Full height
                                const chartWidth = 600;
                                const chartHeight = 180;
                                const margin = { top: 12, right: 12, bottom: 50, left: 45 };
                                const plotWidth = chartWidth - margin.left - margin.right;
                                const plotHeight = chartHeight - margin.top - margin.bottom;

                                const points = trendData.map((point, idx) => {
                                  const x = margin.left + (idx / Math.max(1, trendData.length - 1)) * plotWidth;
                                  const normalizedRank = maxRank === minRank ? 0.5 : (point.rank - minRank) / (maxRank - minRank);
                                  const y = margin.top + (normalizedRank * plotHeight);
                                  return { x, y, rank: point.rank, date: new Date(point.date) };
                                });

                                const pathData = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');

                                // Show all dates on x-axis
                                const dateIndices = trendData.map((_, i) => i);

                                return (
                                  <Box sx={{ position: 'relative', flex: 1, display: 'flex', alignItems: 'center' }}>
                                    <Box sx={{
                                      background: '#2a2a2b',
                                      borderRadius: '8px',
                                      border: '1px solid #3a3a3b',
                                      p: 1,
                                      position: 'relative',
                                      width: '100%',
                                      height: '100%',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      '&:hover': {
                                        border: '1px solid #1E9E5A40'
                                      }
                                    }}>
                                      <svg width={chartWidth} height={chartHeight} viewBox={`0 0 ${chartWidth} ${chartHeight}`} style={{ display: 'block' }}>
                                        {/* Grid lines */}
                                        <defs>
                                          <pattern id={`grid-${idx}`} width={plotWidth / 3} height={plotHeight / 3} patternUnits="userSpaceOnUse" x={margin.left} y={margin.top}>
                                            <path d={`M ${plotWidth / 3} 0 L 0 0 0 ${plotHeight / 3}`} fill="none" stroke="#3a3a3b" strokeWidth="0.3" opacity="0.2" />
                                          </pattern>
                                        </defs>
                                        <rect x={margin.left} y={margin.top} width={plotWidth} height={plotHeight} fill={`url(#grid-${idx})`} />

                                        {/* Y-axis */}
                                        <line x1={margin.left - 2} y1={margin.top} x2={margin.left - 2} y2={margin.top + plotHeight} stroke="#3a3a3b" strokeWidth="0.5" />

                                        {/* Y-axis labels */}
                                        <text x={margin.left - 6} y={margin.top + 3} fontSize="10" fill="#666" textAnchor="end">{Math.round(minRank)}</text>
                                        <text x={margin.left - 6} y={margin.top + plotHeight + 2} fontSize="10" fill="#666" textAnchor="end">{Math.round(maxRank)}</text>

                                        {/* Trend line */}
                                        <path
                                          d={pathData}
                                          fill="none"
                                          stroke="#1E9E5A"
                                          strokeWidth="2"
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                        />

                                        {/* All data points - Large and visible */}
                                        {points.map((point, pidx) => (
                                          <g key={`point-${pidx}`}>
                                            {/* Glow effect */}
                                            <circle
                                              cx={point.x}
                                              cy={point.y}
                                              r="3.5"
                                              fill="#1E9E5A"
                                              opacity="0.15"
                                            />
                                            {/* Main point */}
                                            <circle
                                              cx={point.x}
                                              cy={point.y}
                                              r="2.5"
                                              fill={pidx === 0 ? '#9c9c9c' : '#1E9E5A'}
                                              stroke="#1a1a1b"
                                              strokeWidth="1.5"
                                            />
                                            {/* Highlight dot */}
                                            <circle
                                              cx={point.x}
                                              cy={point.y}
                                              r="1"
                                              fill="#FFFFFF"
                                              opacity="0.8"
                                            />
                                          </g>
                                        ))}

                                        {/* X-axis date labels - All 9 dates */}
                                        {dateIndices.map((dateIdx) => {
                                          const point = points[dateIdx];
                                          const date = trendData[dateIdx];
                                          const dateStr = new Date(date.date).toLocaleDateString('de-DE', { month: '2-digit', day: '2-digit', year: '2-digit' });
                                          return (
                                            <g key={dateIdx}>
                                              <line x1={point.x} y1={margin.top + plotHeight} x2={point.x} y2={margin.top + plotHeight + 2} stroke="#3a3a3b" strokeWidth="0.5" />
                                              <text x={point.x} y={margin.top + plotHeight + 18} fontSize="9" fill="#666" textAnchor="middle">{dateStr}</text>
                                            </g>
                                          );
                                        })}

                                        {/* Interactive hover rects */}
                                        {points.map((point, pidx) => (
                                          <rect
                                            key={`hover-${pidx}`}
                                            x={point.x - 8}
                                            y={margin.top}
                                            width="16"
                                            height={plotHeight}
                                            fill="transparent"
                                            style={{ cursor: 'pointer' }}
                                            onMouseEnter={(e) => {
                                              const parent = e.currentTarget.parentElement;
                                              const tooltips = parent.querySelectorAll('g[data-tooltip]');
                                              tooltips.forEach((t, i) => {
                                                if (i === pidx) t.style.opacity = '1';
                                              });
                                            }}
                                            onMouseLeave={(e) => {
                                              const parent = e.currentTarget.parentElement;
                                              const tooltips = parent.querySelectorAll('g[data-tooltip]');
                                              tooltips.forEach((t) => {
                                                t.style.opacity = '0';
                                              });
                                            }}
                                          />
                                        ))}

                                        {/* Hover tooltips - Smart positioning */}
                                        {points.map((point, pidx) => {
                                          const snapshotData = trendData[pidx];
                                          const bestRankUpToNow = Math.min(...trendData.slice(0, pidx + 1).map(t => t.rank || 100));
                                          const dateStr = point.date.toLocaleDateString('de-DE', { month: '2-digit', day: '2-digit', year: '2-digit' });

                                          // Smart positioning - show below if near top, above if near bottom
                                          const tooltipHeight = 40;
                                          const tooltipWidth = 100;
                                          const showBelow = point.y < margin.top + 35;
                                          const tooltipX = Math.max(margin.left + 15, Math.min(point.x - tooltipWidth / 2, chartWidth - margin.right - 15));
                                          const tooltipY = showBelow ? point.y + 8 : point.y - tooltipHeight - 8;

                                          return (
                                            <g key={`tooltip-${pidx}`} data-tooltip="true" style={{ opacity: '0', transition: 'opacity 150ms ease', pointerEvents: 'none' }}>
                                              {/* Tooltip background */}
                                              <rect
                                                x={tooltipX}
                                                y={tooltipY}
                                                width={tooltipWidth}
                                                height={tooltipHeight}
                                                fill="#1E9E5A"
                                                rx="3"
                                              />
                                              {/* Arrow pointer */}
                                              {showBelow ? (
                                                <polygon
                                                  points={`${point.x},${point.y + 8} ${point.x - 3},${point.y + 2} ${point.x + 3},${point.y + 2}`}
                                                  fill="#1E9E5A"
                                                />
                                              ) : (
                                                <polygon
                                                  points={`${point.x},${point.y - 8} ${point.x - 3},${point.y - 2} ${point.x + 3},${point.y - 2}`}
                                                  fill="#1E9E5A"
                                                />
                                              )}
                                              {/* Date */}
                                              <text
                                                x={tooltipX + tooltipWidth / 2}
                                                y={tooltipY + 10}
                                                fontSize="9"
                                                fill="#FFFFFF"
                                                fontWeight="700"
                                                textAnchor="middle"
                                              >
                                                {dateStr}
                                              </text>
                                              {/* Divider */}
                                              <line
                                                x1={tooltipX + 5}
                                                y1={tooltipY + 14}
                                                x2={tooltipX + tooltipWidth - 5}
                                                y2={tooltipY + 14}
                                                stroke="#ffffff60"
                                                strokeWidth="0.5"
                                              />
                                              {/* Average Rank */}
                                              <text
                                                x={tooltipX + 5}
                                                y={tooltipY + 23}
                                                fontSize="8"
                                                fill="#FFFFFF"
                                                fontWeight="600"
                                              >
                                                Ø:
                                              </text>
                                              <text
                                                x={tooltipX + tooltipWidth - 5}
                                                y={tooltipY + 23}
                                                fontSize="8"
                                                fill="#FFFFFF"
                                                fontWeight="700"
                                                textAnchor="end"
                                              >
                                                #{Math.round(point.rank)}
                                              </text>
                                              {/* Best Rank */}
                                              <text
                                                x={tooltipX + 5}
                                                y={tooltipY + 32}
                                                fontSize="8"
                                                fill="#FFFFFF"
                                                fontWeight="600"
                                              >
                                                Best:
                                              </text>
                                              <text
                                                x={tooltipX + tooltipWidth - 5}
                                                y={tooltipY + 32}
                                                fontSize="8"
                                                fill="#FFFFFF"
                                                fontWeight="700"
                                                textAnchor="end"
                                              >
                                                #{Math.round(bestRankUpToNow)}
                                              </text>
                                              {/* Snapshot # */}
                                              <text
                                                x={tooltipX + tooltipWidth / 2}
                                                y={tooltipY + 40}
                                                fontSize="8"
                                                fill="#ffffff80"
                                                fontWeight="600"
                                                textAnchor="middle"
                                              >
                                                {pidx + 1}/9
                                              </text>
                                            </g>
                                          );
                                        })}
                                      </svg>
                                    </Box>
                                  </Box>
                                );
                              })()}
                            </Box>
                          </Box>
                        );
                      })}
                  </Box>
                </Box>


              </Box>
                        </Box>
                    ) : (
                        <Typography sx={{ color: '#888' }}>Keine Keywords gefunden</Typography>
                    )}
                </DialogContent>

                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={handleCloseActivityModal} sx={{ color: '#FFFFFF' }}>
                        Schließen
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Activity Detail Modal - showing dates in grid */}
            <Dialog
                open={activityDetailOpen}
                onClose={handleCloseActivityDetail}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        background: '#1a1a1b',
                        color: '#FFFFFF'
                    }
                }}
            >
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography sx={{ fontWeight: 700, color: '#FFFFFF', fontSize: '16px' }}>
                        {selectedActivityType}
                    </Typography>
                    <IconButton size="small" onClick={handleCloseActivityDetail} sx={{ color: '#FFFFFF' }}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                <DialogContent sx={{ pt: 2 }}>
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))', gap: 1 }}>
                        {activityDates.map((item, idx) => (
                            <Box
                                key={idx}
                                sx={{
                                    background: '#2a2a2b',
                                    p: 1.5,
                                    borderRadius: '8px',
                                    textAlign: 'center',
                                    border: '1px solid #3a3a3b',
                                    '&:hover': { background: '#3a3a3b' }
                                }}
                            >
                                <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#1E9E5A' }}>
                                    {item.label}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                </DialogContent>

                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={handleCloseActivityDetail} sx={{ color: '#FFFFFF' }}>
                        Schließen
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default TopPerformerPage;